"use client";

import React, { useState, useEffect } from "react";
import { ChevronUp, Plus } from "lucide-react";
import { Controller } from "react-hook-form";
import Button from "./Masters/Button";
import { useGetCityByName, useGetTourDetails } from "@/hooks/useCommon";
import { useAuth } from "@/store/auth";

interface HotelCity {
  id: string;
  city: string;
  nights: string;
}

interface CityOption {
  city_id?: number;
  id?: number;
  citystatecountry?: string;
  city_name?: string;
  name?: string;
}

interface FeatureInformationProps {
  register: any;
  control: any;
  errors: any;
  watch: any;
  setFeatureSelectedStartCities: React.Dispatch<React.SetStateAction<any[]>>;
  setFeatureSelectedEndCities: React.Dispatch<React.SetStateAction<any[]>>;
  setFeatureSelectedVisitingCities: React.Dispatch<React.SetStateAction<any[]>>;
  featureSelectedStartCities: any[];
  featureSelectedEndCities: any[];
  featureSelectedVisitingCities: any[];
}

const FeatureInformation: React.FC<FeatureInformationProps> = ({
  register,
  control,
  errors,
  watch,
  setFeatureSelectedStartCities,
  setFeatureSelectedEndCities,
  setFeatureSelectedVisitingCities,
  featureSelectedStartCities,
  featureSelectedEndCities,
  featureSelectedVisitingCities,
}) => {
  const { user } = useAuth();
  const [hotelCities, setHotelCities] = useState<HotelCity[]>([]);
  const [newHotelCity, setNewHotelCity] = useState("");
  const [newHotelNights, setNewHotelNights] = useState("");

  // Search inputs
  const [startCitySearch, setStartCitySearch] = useState("");
  const [endCitySearch, setEndCitySearch] = useState("");
  const [visitingCitySearch, setVisitingCitySearch] = useState("");

  // Dropdown visibility
  const [showStartCityDropdown, setShowStartCityDropdown] = useState(false);
  const [showEndCityDropdown, setShowEndCityDropdown] = useState(false);
  const [showVisitingCityDropdown, setShowVisitingCityDropdown] = useState(false);

  // Tour list for "Tour Package"
  const { mutate: fetchTours, data: tourResults } = useGetTourDetails();
  const selectedTourId = watch("tour_package_id");

  // City search mutations
  const {
    mutate: searchStartCities,
    data: startCityResults,
    isPending: isSearchingStartCities,
  } = useGetCityByName();
  const {
    mutate: searchEndCities,
    data: endCityResults,
    isPending: isSearchingEndCities,
  } = useGetCityByName();
  const {
    mutate: searchVisitingCities,
    data: visitingCityResults,
    isPending: isSearchingVisitingCities,
  } = useGetCityByName();

  // Normalize tours response
  const tours = React.useMemo(() => {
    if (!tourResults) return [];
    const anyData: any = tourResults;
    if (Array.isArray(anyData.data)) return anyData.data;
    if (Array.isArray(anyData.response?.data)) return anyData.response.data;
    if (Array.isArray(anyData.responseData?.response?.data)) {
      return anyData.responseData.response.data;
    }
    return [];
  }, [tourResults]);

  // Fetch tours for current company
  useEffect(() => {
    fetchTours(
      {},
      {
        onSuccess: (data: any) => {
          console.log("Tour details fetched for Tour Package field:", data);
        },
        onError: (err: any) => {
          console.error("Error fetching tour details:", err?.response?.data || err);
        },
      }
    );
  }, [fetchTours]);

  // Helpers to normalize API responses
  const normalizeCityList = (results: any): CityOption[] => {
    if (!results) return [];
    if (Array.isArray(results)) return results;
    if (Array.isArray(results?.data)) return results.data;
    if (Array.isArray(results?.cities)) return results.cities;
    if (Array.isArray(results?.responseData?.response?.data)) {
      return results.responseData.response.data;
    }
    if (Array.isArray(results?.responseData?.response)) {
      return results.responseData.response;
    }
    return [];
  };

  const startCitiesList = React.useMemo(
    () => normalizeCityList(startCityResults),
    [startCityResults]
  );
  const endCitiesList = React.useMemo(() => normalizeCityList(endCityResults), [endCityResults]);
  const visitingCitiesList = React.useMemo(
    () => normalizeCityList(visitingCityResults),
    [visitingCityResults]
  );

  // Trigger API when user types
  useEffect(() => {
    if (startCitySearch.length >= 2) {
      searchStartCities({ cityname: startCitySearch });
      setShowStartCityDropdown(true);
    } else {
      setShowStartCityDropdown(false);
    }
  }, [startCitySearch, searchStartCities]);

  useEffect(() => {
    if (endCitySearch.length >= 2) {
      searchEndCities({ cityname: endCitySearch });
      setShowEndCityDropdown(true);
    } else {
      setShowEndCityDropdown(false);
    }
  }, [endCitySearch, searchEndCities]);

  useEffect(() => {
    if (visitingCitySearch.length >= 2) {
      searchVisitingCities({ cityname: visitingCitySearch });
      setShowVisitingCityDropdown(true);
    } else {
      setShowVisitingCityDropdown(false);
    }
  }, [visitingCitySearch, searchVisitingCities]);

  // Selection handlers
  const handleStartCitySelect = (city: CityOption) => {
    const cityId = city.city_id || city.id;
    if (!featureSelectedStartCities.find((c) => (c.city_id || c.id) === cityId)) {
      setFeatureSelectedStartCities([...featureSelectedStartCities, city]);
    }
    setStartCitySearch("");
    setShowStartCityDropdown(false);
  };

  const handleEndCitySelect = (city: CityOption) => {
    const cityId = city.city_id || city.id;
    if (!featureSelectedEndCities.find((c) => (c.city_id || c.id) === cityId)) {
      setFeatureSelectedEndCities([...featureSelectedEndCities, city]);
    }
    setEndCitySearch("");
    setShowEndCityDropdown(false);
  };

  const handleVisitingCitySelect = (city: CityOption) => {
    const cityId = city.city_id || city.id;
    if (!featureSelectedVisitingCities.find((c) => (c.city_id || c.id) === cityId)) {
      setFeatureSelectedVisitingCities([...featureSelectedVisitingCities, city]);
    }
    setVisitingCitySearch("");
    setShowVisitingCityDropdown(false);
  };

  // Remove handlers
  const removeStartCity = (cityId: number) => {
    setFeatureSelectedStartCities(featureSelectedStartCities.filter((c) => (c.city_id || c.id) !== cityId));
  };

  const removeEndCity = (cityId: number) => {
    setFeatureSelectedEndCities(featureSelectedEndCities.filter((c) => (c.city_id || c.id) !== cityId));
  };

  const removeVisitingCity = (cityId: number) => {
    setFeatureSelectedVisitingCities(
      featureSelectedVisitingCities.filter((c) => (c.city_id || c.id) !== cityId)
    );
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        !target.closest(".feature-start-city-dropdown") &&
        !target.closest(".feature-start-input")
      ) {
        setShowStartCityDropdown(false);
      }
      if (!target.closest(".feature-end-city-dropdown") && !target.closest(".feature-end-input")) {
        setShowEndCityDropdown(false);
      }
      if (
        !target.closest(".feature-visiting-city-dropdown") &&
        !target.closest(".feature-visiting-input")
      ) {
        setShowVisitingCityDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleAddHotelCity = () => {
    if (newHotelCity.trim() && newHotelNights.trim()) {
      setHotelCities([
        ...hotelCities,
        {
          id: Date.now().toString(),
          city: newHotelCity,
          nights: newHotelNights,
        },
      ]);
      setNewHotelCity("");
      setNewHotelNights("");
    }
  };

  const handleRemoveHotelCity = (id: string) => {
    setHotelCities(hotelCities.filter((city) => city.id !== id));
  };

  const renderCityName = (city: CityOption) =>
    city.citystatecountry || city.city_name || city.name || "City";

  // Update form value when hotel cities change
  useEffect(() => {
    if (hotelCities.length > 0) {
      // You can store hotel cities as JSON string or handle separately
      // For now, we'll just keep it in local state
    }
  }, [hotelCities]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-800">Tours Feature</h2>
        <button
          type="button"
          className="p-1 bg-gray-100 rounded hover:bg-gray-200"
          title="Collapse"
        >
          <ChevronUp className="w-4 h-4 text-gray-600" />
        </button>
      </div>

      {/* Two Column Form */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
        {/* Left Column */}
        <div className="space-y-3">
          <div>
            <label className="block text-[11px] font-medium text-gray-700 mb-1">
              Tour Package *
            </label>
            <select
              {...register("tour_package_id")}
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">-- Select Tours --</option>
              {Array.isArray(tours) &&
                tours.map((tour: any) => (
                  <option key={tour.id} value={tour.id}>
                    {tour.tours_name}
                  </option>
                ))}
            </select>
            {errors.tour_package_id && (
              <span className="text-red-500 text-[10px]">{errors.tour_package_id.message}</span>
            )}
          </div>

          {/* Start City with city API */}
          <div className="relative feature-start-city-dropdown">
            <label className="block text-[11px] font-medium text-gray-700 mb-1">
              Start City *
            </label>
            <div className="relative w-full border border-gray-300 rounded bg-white min-h-[60px]">
              {featureSelectedStartCities.length > 0 && (
                <div className="flex flex-wrap gap-1.5 p-2 pb-0">
                  {featureSelectedStartCities.map((city) => {
                    const cityId = city.city_id || city.id!;
                    return (
                      <span
                        key={cityId}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-100 text-blue-800 rounded-md text-[10px] font-medium border border-blue-200"
                      >
                        {renderCityName(city)}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeStartCity(cityId);
                          }}
                          className="hover:text-red-600 hover:bg-red-100 rounded-full w-4 h-4 flex items-center justify-center text-xs font-bold"
                          title="Remove city"
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              <textarea
                value={startCitySearch}
                onChange={(e) => setStartCitySearch(e.target.value)}
                onFocus={() => startCitySearch.length >= 2 && setShowStartCityDropdown(true)}
                placeholder={
                  featureSelectedStartCities.length > 0
                    ? "Type to search more cities..."
                    : "Type to search cities..."
                }
                rows={featureSelectedStartCities.length > 0 ? 1 : 2}
                className="feature-start-input w-full border-0 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-0 resize-none"
                style={{ minHeight: featureSelectedStartCities.length > 0 ? "32px" : "48px" }}
              />
            </div>
            {showStartCityDropdown && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-48 overflow-y-auto">
                {isSearchingStartCities ? (
                  <div className="px-3 py-2 text-xs text-gray-500">Searching...</div>
                ) : startCitiesList.length > 0 ? (
                  startCitiesList
                    .filter((city: CityOption) => {
                      const cityId = city.city_id || city.id;
                      return !featureSelectedStartCities.find((c) => (c.city_id || c.id) === cityId);
                    })
                    .map((city: CityOption) => {
                      const cityId = city.city_id || city.id!;
                      return (
                        <div
                          key={cityId}
                          onClick={() => handleStartCitySelect(city)}
                          className="px-3 py-2 text-xs hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          {renderCityName(city)}
                        </div>
                      );
                    })
                ) : startCitySearch.length >= 2 ? (
                  <div className="px-3 py-2 text-xs text-gray-500">No cities found</div>
                ) : null}
              </div>
            )}
          </div>

          {/* Visiting Cities with city API */}
          <div className="relative feature-visiting-city-dropdown">
            <label className="block text-[11px] font-medium text-gray-700 mb-1">
              Visiting Cities *
            </label>
            <div className="relative w-full border border-gray-300 rounded bg-white min-h-[60px]">
              {featureSelectedVisitingCities.length > 0 && (
                <div className="flex flex-wrap gap-1.5 p-2 pb-0">
                  {featureSelectedVisitingCities.map((city) => {
                    const cityId = city.city_id || city.id!;
                    return (
                      <span
                        key={cityId}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-indigo-100 text-indigo-800 rounded-md text-[10px] font-medium border border-indigo-200"
                      >
                        {renderCityName(city)}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeVisitingCity(cityId);
                          }}
                          className="hover:text-red-600 hover:bg-red-100 rounded-full w-4 h-4 flex items-center justify-center text-xs font-bold"
                          title="Remove city"
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              <textarea
                value={visitingCitySearch}
                onChange={(e) => setVisitingCitySearch(e.target.value)}
                onFocus={() => visitingCitySearch.length >= 2 && setShowVisitingCityDropdown(true)}
                placeholder={
                  featureSelectedVisitingCities.length > 0
                    ? "Type to search more cities..."
                    : "Type to search cities..."
                }
                rows={featureSelectedVisitingCities.length > 0 ? 1 : 2}
                className="feature-visiting-input w-full border-0 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-0 resize-none"
                style={{ minHeight: featureSelectedVisitingCities.length > 0 ? "32px" : "48px" }}
              />
            </div>
            {showVisitingCityDropdown && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-48 overflow-y-auto">
                {isSearchingVisitingCities ? (
                  <div className="px-3 py-2 text-xs text-gray-500">Searching...</div>
                ) : visitingCitiesList.length > 0 ? (
                  visitingCitiesList
                    .filter((city: CityOption) => {
                      const cityId = city.city_id || city.id;
                      return !featureSelectedVisitingCities.find((c) => (c.city_id || c.id) === cityId);
                    })
                    .map((city: CityOption) => {
                      const cityId = city.city_id || city.id!;
                      return (
                        <div
                          key={cityId}
                          onClick={() => handleVisitingCitySelect(city)}
                          className="px-3 py-2 text-xs hover:bg-indigo-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          {renderCityName(city)}
                        </div>
                      );
                    })
                ) : visitingCitySearch.length >= 2 ? (
                  <div className="px-3 py-2 text-xs text-gray-500">No cities found</div>
                ) : null}
              </div>
            )}
          </div>

          <div>
            <label className="block text-[11px] font-medium text-gray-700 mb-1">
              No. of Accommodates *
            </label>
            <input
              {...register("accommodates")}
              type="number"
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {errors.accommodates && (
              <span className="text-red-500 text-[10px]">{errors.accommodates.message}</span>
            )}
          </div>

          <div>
            <label className="block text-[11px] font-medium text-gray-700 mb-1">
              Pick-up service *
            </label>
            <input
              {...register("pickup_service")}
              type="text"
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {errors.pickup_service && (
              <span className="text-red-500 text-[10px]">{errors.pickup_service.message}</span>
            )}
          </div>

          <div>
            <label className="block text-[11px] font-medium text-gray-700 mb-1">
              Addon facilities
            </label>
            <input
              {...register("supplementary_room")}
              type="text"
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-[11px] font-medium text-gray-700 mb-1">
              View | Location Type *
            </label>
            <input
              {...register("location_type")}
              type="text"
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {errors.location_type && (
              <span className="text-red-500 text-[10px]">{errors.location_type.message}</span>
            )}
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-3">
          {/* End City with city API */}
          <div className="relative feature-end-city-dropdown">
            <label className="block text-[11px] font-medium text-gray-700 mb-1">
              End City *
            </label>
            <div className="relative w-full border border-gray-300 rounded bg-white min-h-[60px]">
              {featureSelectedEndCities.length > 0 && (
                <div className="flex flex-wrap gap-1.5 p-2 pb-0">
                  {featureSelectedEndCities.map((city) => {
                    const cityId = city.city_id || city.id!;
                    return (
                      <span
                        key={cityId}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-100 text-green-800 rounded-md text-[10px] font-medium border border-green-200"
                      >
                        {renderCityName(city)}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeEndCity(cityId);
                          }}
                          className="hover:text-red-600 hover:bg-red-100 rounded-full w-4 h-4 flex items-center justify-center text-xs font-bold"
                          title="Remove city"
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              <textarea
                value={endCitySearch}
                onChange={(e) => setEndCitySearch(e.target.value)}
                onFocus={() => endCitySearch.length >= 2 && setShowEndCityDropdown(true)}
                placeholder={
                  featureSelectedEndCities.length > 0
                    ? "Type to search more cities..."
                    : "Type to search cities..."
                }
                rows={featureSelectedEndCities.length > 0 ? 1 : 2}
                className="feature-end-input w-full border-0 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-0 resize-none"
                style={{ minHeight: featureSelectedEndCities.length > 0 ? "32px" : "48px" }}
              />
            </div>
            {showEndCityDropdown && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-48 overflow-y-auto">
                {isSearchingEndCities ? (
                  <div className="px-3 py-2 text-xs text-gray-500">Searching...</div>
                ) : endCitiesList.length > 0 ? (
                  endCitiesList
                    .filter((city: CityOption) => {
                      const cityId = city.city_id || city.id;
                      return !featureSelectedEndCities.find((c) => (c.city_id || c.id) === cityId);
                    })
                    .map((city: CityOption) => {
                      const cityId = city.city_id || city.id!;
                      return (
                        <div
                          key={cityId}
                          onClick={() => handleEndCitySelect(city)}
                          className="px-3 py-2 text-xs hover:bg-green-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          {renderCityName(city)}
                        </div>
                      );
                    })
                ) : endCitySearch.length >= 2 ? (
                  <div className="px-3 py-2 text-xs text-gray-500">No cities found</div>
                ) : null}
              </div>
            )}
          </div>

          <div>
            <label className="block text-[11px] font-medium text-gray-700 mb-1">
              Schedules / Operating *
            </label>
            <input
              {...register("scheduled_on")}
              type="text"
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {errors.scheduled_on && (
              <span className="text-red-500 text-[10px]">{errors.scheduled_on.message}</span>
            )}
          </div>

          <div>
            <label className="block text-[11px] font-medium text-gray-700 mb-1">
              Multilingual guide tape *
            </label>
            <input
              {...register("multilingual_guide")}
              type="text"
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {errors.multilingual_guide && (
              <span className="text-red-500 text-[10px]">{errors.multilingual_guide.message}</span>
            )}
          </div>

          <div>
            <label className="block text-[11px] font-medium text-gray-700 mb-1">
              Drop-off service *
            </label>
            <input
              {...register("drop_off_service")}
              type="text"
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {errors.drop_off_service && (
              <span className="text-red-500 text-[10px]">{errors.drop_off_service.message}</span>
            )}
          </div>

          <div>
            <label className="block text-[11px] font-medium text-gray-700 mb-1">
              Entertainment *
            </label>
            <input
              {...register("entertainments")}
              type="text"
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {errors.entertainments && (
              <span className="text-red-500 text-[10px]">{errors.entertainments.message}</span>
            )}
          </div>
        </div>
      </div>

      {/* Hotel City Section */}
      <div className="pt-4 border-t border-gray-200 mt-4">
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className="block text-[11px] font-medium text-gray-700 mb-1">
              Add Hotel City *
            </label>
            <input
              type="text"
              value={newHotelCity}
              onChange={(e) => setNewHotelCity(e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Enter city name"
            />
          </div>
          <div className="w-32">
            <label className="block text-[11px] font-medium text-gray-700 mb-1">
              No. of Nights *
            </label>
            <input
              type="number"
              value={newHotelNights}
              onChange={(e) => setNewHotelNights(e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Nights"
            />
          </div>
          <Button
            size="sm"
            variant="success"
            onClick={handleAddHotelCity}
            icon={<Plus className="w-4 h-4" />}
            iconPosition="left"
          >
            +Add City
          </Button>
        </div>

        {/* Display Added Hotel Cities */}
        {hotelCities.length > 0 && (
          <div className="mt-3 space-y-2">
            {hotelCities.map((city) => (
              <div
                key={city.id}
                className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200"
              >
                <span className="text-xs text-gray-700">
                  {city.city} - {city.nights} nights
                </span>
                <button
                  type="button"
                  onClick={() => handleRemoveHotelCity(city.id)}
                  className="text-red-500 hover:text-red-700 text-xs"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Toggle Switches */}
      <div className="pt-4 border-t border-gray-200 mt-4">
        <div className="flex justify-end gap-6">
          <div className="flex items-center gap-2 bg-gray-100 px-4 py-2 rounded">
            <label className="flex items-center gap-2 cursor-pointer">
              <span className="text-[11px] font-medium text-gray-700">
                Lowest Cost of Hotels
              </span>
              <Controller
                name="lowest_cost_hotels"
                control={control}
                render={({ field }) => (
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={field.value || false}
                      onChange={(e) => field.onChange(e.target.checked)}
                      className="sr-only"
                    />
                    <div
                      className={`w-10 h-6 rounded-full transition-colors ${
                        field.value ? "bg-red-500" : "bg-gray-300"
                      }`}
                    >
                      <div
                        className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                          field.value ? "translate-x-4" : "translate-x-0.5"
                        } mt-0.5`}
                      />
                    </div>
                  </div>
                )}
              />
            </label>
          </div>

          <div className="flex items-center gap-2 bg-gray-100 px-4 py-2 rounded">
            <label className="flex items-center gap-2 cursor-pointer">
              <span className="text-[11px] font-medium text-gray-700">
                Meal Plan Wise Hotels
              </span>
              <Controller
                name="meal_plan_wise_hotels"
                control={control}
                render={({ field }) => (
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={field.value || false}
                      onChange={(e) => field.onChange(e.target.checked)}
                      className="sr-only"
                    />
                    <div
                      className={`w-10 h-6 rounded-full transition-colors ${
                        field.value ? "bg-red-500" : "bg-gray-300"
                      }`}
                    >
                      <div
                        className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                          field.value ? "translate-x-4" : "translate-x-0.5"
                        } mt-0.5`}
                      />
                    </div>
                  </div>
                )}
              />
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureInformation;
